import { useState, useEffect, useCallback } from "react";
import { getProgress as getStoredProgress, setProgress as setStoredProgress } from "../services/localStorage";

export interface ProgressEntry {
    viewed: string[];
    amountOfChildren: number;
}

export interface ProgressState {
    [parentId: string]: ProgressEntry;
}

interface UseViewedDataProgressReturn {
    progress: ProgressState;
    createParent: (parentId: string | number, totalChildren: number) => void;
    markChildViewed: (parentId: string | number, childId: string | number) => void;
    shouldBeVisible: (parentId: string | number, idx: number) => boolean;
    getProgress: (parentId: string | number) => ProgressEntry | undefined;
    hasBeenViewed: (parentId: string | number, childId: string | number) => boolean;
}

export function useViewedDataProgress(slug: string | undefined): UseViewedDataProgressReturn {
    const [progress, setProgress] = useState<ProgressState>({});

    useEffect(() => {
        const stored = getStoredProgress(slug);
        setProgress(stored ?? {});
    }, [slug]);

    const createParent = useCallback((parentId: string | number, totalChildren: number): void => {
        const parentKey = String(parentId);
        setProgress((prevProgress) => {
            if (prevProgress[parentKey]) {
                if (prevProgress[parentKey].amountOfChildren !== totalChildren) {
                    const newProgress: ProgressState = {
                        ...prevProgress,
                        [parentKey]: {
                            ...prevProgress[parentKey],
                            amountOfChildren: totalChildren
                        }
                    };
                    setStoredProgress(slug, newProgress);
                    return newProgress;
                }
                return prevProgress;
            }

            const newProgress: ProgressState = {
                ...prevProgress,
                [parentKey]: {
                    viewed: [],
                    amountOfChildren: totalChildren
                }
            };
            setStoredProgress(slug, newProgress);
            return newProgress;
        });
    }, [slug]);

    const markChildViewed = useCallback((parentId: string | number, childId: string | number): void => {
        const parentKey = String(parentId);
        const childKey = String(childId);
        setProgress((prevProgress) => {
            const prevViewed = prevProgress[parentKey]?.viewed || [];
            if (prevViewed.includes(childKey)) return prevProgress;

            const newProgress: ProgressState = {
                ...prevProgress,
                [parentKey]: {
                    ...prevProgress[parentKey],
                    viewed: [...prevViewed, childKey]
                }
            };
            setStoredProgress(slug, newProgress);
            return newProgress;
        });
    }, [slug]);

    const getProgress = useCallback((parentId: string | number): ProgressEntry | undefined => {
        return progress[String(parentId)];
    }, [progress]);

    const shouldBeVisible = (parentId: string | number, idx: number): boolean => {
        if (idx === 0) return true;

        if (idx > (progress[String(parentId)]?.viewed?.length ?? 0)) return false;
        return true;
    };

    const hasBeenViewed = (parentId: string | number, childId: string | number): boolean => {
        const entry = progress[String(parentId)];
        if (!entry || !entry.viewed) return false;
        return entry.viewed.includes(String(childId));
    };

    return {
        progress,
        createParent,
        markChildViewed,
        shouldBeVisible,
        getProgress,
        hasBeenViewed
    };
}
